import React from "react";
import { Link } from "react-router-dom";
import { Moon, Sun, Sparkles, ArrowRight, CalendarDays } from "lucide-react";
import { Reveal } from "./Reveal";
import { useLang } from "../i18n/LanguageContext";
import { useBooking } from "../context/BookingContext";
import { useFestivals, festivalName, daysUntil, formatFestivalDate } from "../lib/festivals";
import ReminderButton from "./ReminderButton";

const KIND_ICON = { purnima: Sun, kaushiki: Sparkles, mauni: Moon, diwali: Sparkles, amavasya: Moon, somvati: Moon };

export function FestivalCard({ f, index = 0, compact = false }) {
  const { t, lang } = useLang();
  const { openBooking } = useBooking();
  const Ico = KIND_ICON[f.kind] || Moon;
  const days = daysUntil(f.date);
  const name = festivalName(f, lang);

  const when = days <= 0 ? t("today") : days === 1 ? t("tomorrow") : `${days} ${t("days_away")}`;

  return (
    <Reveal
      variant="blur"
      delay={(index % 4) * 0.07}
      className={`glass hover-lift rounded-[1.5rem] p-6 flex flex-col ${
        f.major ? "ring-1 ring-tara-gold/50 bg-white/78" : ""
      }`}
      data-testid={`festival-card-${f.id}`}
    >
      <div className="flex items-start justify-between mb-4">
        <span
          className={`flex h-11 w-11 items-center justify-center rounded-2xl ${
            f.major
              ? "bg-gradient-to-br from-tara-vermilion to-tara-gold text-white"
              : "bg-white/80 border border-tara-gold/30 text-tara-gold"
          }`}
        >
          <Ico className="h-5 w-5" />
        </span>
        <span className="badge-gold rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em]">{when}</span>
      </div>

      <h3 className="font-display text-2xl font-semibold text-tara-ink leading-snug mb-1">{name}</h3>
      <p className="flex items-center gap-1.5 text-xs text-tara-muted mb-3">
        <CalendarDays className="h-3.5 w-3.5" /> {formatFestivalDate(f.date)}
        {f.time_ist && <span className="text-tara-gold/80">· {f.time_ist}</span>}
      </p>

      {!compact && f.note && <p className="text-sm text-tara-body leading-relaxed mb-5">{f.note}</p>}

      <div className="mt-auto space-y-2.5">
        <button
          onClick={() => openBooking(f.service_id || "maa-tara-puja", `${name} — ${formatFestivalDate(f.date)}`)}
          className="btn-sacred flex w-full items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-serif font-semibold"
          data-testid={`festival-book-${f.id}`}
        >
          {t("festival_book")} <ArrowRight className="h-4 w-4" />
        </button>
        <div className="flex justify-center">
          <ReminderButton festival={f} compact />
        </div>
      </div>
    </Reveal>
  );
}

export default function FestivalStrip() {
  const { t } = useLang();
  const { items, loading } = useFestivals(8);
  if (loading || items.length === 0) return null;

  const featured = [...items].sort((a, b) => (b.major ? 1 : 0) - (a.major ? 1 : 0)).slice(0, 4);

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 sm:pb-24" data-testid="festival-strip">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5 mb-12">
        <div className="max-w-xl">
          <span className="text-[11px] font-serif tracking-[0.28em] uppercase text-tara-goldDeep">{t("festivals_eyebrow")}</span>
          <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-tara-ink mt-3">{t("festivals_title")}</h2>
          <div className="rule-gold my-5" />
          <p className="text-base text-tara-body">{t("festivals_sub")}</p>
        </div>
        <Link
          to="/festivals"
          className="inline-flex items-center gap-1.5 text-tara-goldDeep font-serif font-semibold hover:gap-3 transition-all"
          data-testid="festivals-view-all"
        >
          {t("view_all_dates")} <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {featured.map((f, i) => (
          <FestivalCard key={f.id} f={f} index={i} compact />
        ))}
      </div>
    </section>
  );
}
